import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, MousePointerClick, Package, ShoppingBag, Music2, Users, Store } from "lucide-react";
import { api, fileUrl, rupiah } from "../../lib/api";
import { usePolling } from "../../hooks/usePolling";
import { useAuth } from "../../context/AuthContext";
import { StatCard } from "../../components/StatCard";
import { ClicksChart } from "../../components/ClicksChart";
import { PageHeader } from "../../components/PageHeader";

const TopProducts = ({ items }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 fade-up" data-testid="top-products">
    <h3 className="font-bold mb-4">Produk terpopuler</h3>
    {!items.length && <p className="text-sm text-slate-500">Belum ada produk.</p>}
    <ul className="space-y-3">
      {items.map((p) => (
        <li key={p.product_id}>
          <Link to={`/dashboard/products/${p.product_id}`} className="flex items-center gap-3 group">
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-slate-100">
              {p.photos?.[0] && <img src={fileUrl(p.photos[0])} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold group-hover:underline">{p.name}</p>
              <p className="text-xs text-slate-500">{rupiah(p.price)}</p>
            </div>
            <span className="font-mono text-sm font-semibold">{(p.clicks_shopee || 0) + (p.clicks_tiktok || 0)}</span>
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const TokoOverview = ({ name }) => {
  const [s, setS] = useState(null);
  usePolling(async () => {
    const { data } = await api.get("/dashboard/stats");
    setS(data);
  }, 5000);
  return (
    <div>
      <PageHeader title={`Halo, ${name}`} subtitle="Statistik toko affiliate kamu, diperbarui otomatis." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard label="Jumlah produk" value={s?.total_products} icon={Package} testId="stat-total-products" />
        <StatCard label="Total klik affiliate" value={s?.total_clicks} icon={MousePointerClick} testId="stat-total-clicks" />
        <StatCard label="Klik Shopee" value={s?.shopee_clicks} icon={ShoppingBag} accent="text-[#EE4D2D]" testId="stat-shopee-clicks" />
        <StatCard label="Klik TikTok" value={s?.tiktok_clicks} icon={Music2} testId="stat-tiktok-clicks" />
      </div>
      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 min-w-0"><ClicksChart data={s?.daily || []} /></div>
        <div className="space-y-6 min-w-0">
          <StatCard label="Produk dilihat" value={s?.views} icon={Eye} testId="stat-views" hint="Jumlah detail produk dibuka pengunjung" />
          <TopProducts items={s?.top_products || []} />
        </div>
      </div>
    </div>
  );
};

const AdminOverview = () => {
  const [s, setS] = useState(null);
  usePolling(async () => {
    const { data } = await api.get("/admin/stats");
    setS(data);
  }, 5000);
  return (
    <div>
      <PageHeader title="Dashboard Admin" subtitle="Ringkasan seluruh platform Jufi." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard label="Total member" value={s?.members} icon={Users} testId="admin-stat-members" hint={`${s?.active_members ?? 0} aktif`} />
        <StatCard label="Total toko" value={s?.shops} icon={Store} testId="admin-stat-shops" />
        <StatCard label="Total produk" value={s?.products} icon={Package} testId="admin-stat-products" />
        <StatCard label="Total klik" value={s?.total_clicks} icon={MousePointerClick} testId="admin-stat-clicks" />
      </div>
      <div className="mt-6 min-w-0"><ClicksChart data={s?.daily || []} title="Klik affiliate harian (semua toko)" /></div>
    </div>
  );
};

export default function Overview() {
  const { user } = useAuth();
  return user.role === "admin" ? <AdminOverview /> : <TokoOverview name={user.name} />;
}
